/**
 * SolarCalculator - Handles sunrise and sunset calculations using SunCalc library
 * Provides accurate solar time calculations with caching and timezone support
 */

import { SolarCalculator as SolarCalculatorInterface } from './interfaces/index.js';
import SunCalc from 'suncalc';

/**
 * SolarCalculator implementation with SunCalc library integration
 */
export class SolarCalculator extends SolarCalculatorInterface {
  constructor(config = {}) {
    super();
    
    // Configuration with defaults
    this.config = {
      cacheExpiration: config.cacheExpiration || 24 * 60 * 60 * 1000, // 24 hours default
      accuracyThreshold: config.accuracyThreshold || 2, // 2 minutes accuracy
      enableCaching: config.enableCaching !== false, // enabled by default
      maxCacheSize: config.maxCacheSize || 100, // Maximum cached calculations
      coordinatePrecision: config.coordinatePrecision || 4, // Decimal places for coordinates
      ...config
    };
    
    // Internal cache for calculations
    this.calculationCache = new Map();
    this.cacheService = config.cacheService || null;
    
    // Statistics for monitoring
    this.stats = {
      totalCalculations: 0,
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0
    };
  }

  /**
   * Calculate solar times for a given date and location using SunCalc
   * @param {Date} date - Date for calculation
   * @param {number} latitude - Latitude in decimal degrees (-90 to 90)
   * @param {number} longitude - Longitude in decimal degrees (-180 to 180)
   * @returns {SolarTimes} Solar times object with sunrise, sunset, and date
   */
  calculateSolarTimes(date, latitude, longitude) {
    // Validate inputs
    this.validateInputs(date, latitude, longitude);
    
    // Generate cache key
    const cacheKey = this.generateCacheKey(date, latitude, longitude);
    
    // Check cache first if enabled
    if (this.config.enableCaching) {
      const cachedResult = this.getCachedCalculation(cacheKey);
      if (cachedResult) {
        this.stats.cacheHits++;
        return cachedResult;
      }
      this.stats.cacheMisses++;
    }
    
    try {
      // Normalize date to start of day in local timezone to handle midnight rollovers
      const normalizedDate = this.normalizeDateForCalculation(date);
      
      // Use SunCalc to get solar times
      const times = SunCalc.getTimes(normalizedDate, latitude, longitude);
      
      // Validate SunCalc results
      this.validateSunCalcResults(times, normalizedDate, latitude, longitude);
      
      // Create result object
      const solarTimes = {
        sunrise: new Date(times.sunrise),
        sunset: new Date(times.sunset),
        date: new Date(normalizedDate)
      };
      
      // Handle edge cases (polar regions, invalid times)
      this.handleEdgeCases(solarTimes, latitude, longitude, normalizedDate);
      
      // Cache the result if enabled
      if (this.config.enableCaching) {
        this.cacheCalculation(cacheKey, solarTimes);
      }
      
      this.stats.totalCalculations++;
      
      return solarTimes;
      
    } catch (error) {
      this.stats.errors++;
      console.error('Solar calculation failed:', error);
      throw new Error(`Failed to calculate solar times: ${error.message}`);
    }
  }

  /**
   * Get time delta from sunrise with proper formatting
   * @param {Date} currentTime - Current time
   * @param {SolarTimes} solarTimes - Solar times for the day
   * @returns {TimeDelta} Time delta from sunrise
   */
  getSunriseDelta(currentTime, solarTimes) {
    this.validateTimeInputs(currentTime, solarTimes);
    
    const sunrise = solarTimes.sunrise;
    const timeDiff = currentTime.getTime() - sunrise.getTime();
    
    // Handle date boundary cases
    const adjustedDiff = this.handleDateBoundaries(timeDiff, currentTime, sunrise);
    
    const hours = Math.floor(Math.abs(adjustedDiff) / (1000 * 60 * 60));
    const minutes = Math.floor((Math.abs(adjustedDiff) % (1000 * 60 * 60)) / (1000 * 60));
    const isPast = adjustedDiff > 0;
    
    // Generate human-readable description
    const description = this.formatTimeDelta(hours, minutes, isPast, 'sunrise');
    
    return {
      hours,
      minutes,
      isPast,
      description
    };
  }

  /**
   * Get time delta to/from sunset with proper formatting
   * @param {Date} currentTime - Current time
   * @param {SolarTimes} solarTimes - Solar times for the day
   * @returns {TimeDelta} Time delta to/from sunset
   */
  getSunsetDelta(currentTime, solarTimes) {
    this.validateTimeInputs(currentTime, solarTimes);
    
    const sunset = solarTimes.sunset;
    const timeDiff = sunset.getTime() - currentTime.getTime();
    
    // Handle date boundary cases
    const adjustedDiff = this.handleDateBoundaries(timeDiff, currentTime, sunset);
    
    const hours = Math.floor(Math.abs(adjustedDiff) / (1000 * 60 * 60));
    const minutes = Math.floor((Math.abs(adjustedDiff) % (1000 * 60 * 60)) / (1000 * 60));
    const isPast = adjustedDiff < 0;
    
    // Generate human-readable description
    const description = this.formatTimeDelta(hours, minutes, isPast, 'sunset');
    
    return {
      hours,
      minutes,
      isPast,
      description
    };
  }

  /**
   * Get calculation statistics
   * @returns {Object} Statistics object
   */
  getStats() {
    return {
      ...this.stats,
      cacheSize: this.calculationCache.size,
      hitRate: this.stats.totalCalculations > 0 ? 
        (this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses)) * 100 : 0
    };
  }

  /**
   * Clear calculation cache
   */
  clearCache() {
    this.calculationCache.clear();
    console.log('Solar calculation cache cleared');
  }

  /**
   * Set cache service for persistent storage
   * @param {CacheService} cacheService - Cache service instance
   */
  setCacheService(cacheService) {
    this.cacheService = cacheService;
  }

  // Private methods

  /**
   * Validate input parameters for solar calculations
   * @private
   * @param {Date} date - Date to validate
   * @param {number} latitude - Latitude to validate
   * @param {number} longitude - Longitude to validate
   */
  validateInputs(date, latitude, longitude) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error('Invalid date provided');
    }
    
    if (typeof latitude !== 'number' || isNaN(latitude)) {
      throw new Error('Invalid latitude provided');
    }
    
    if (typeof longitude !== 'number' || isNaN(longitude)) {
      throw new Error('Invalid longitude provided');
    }
    
    if (latitude < -90 || latitude > 90) {
      throw new Error('Latitude must be between -90 and 90 degrees');
    }
    
    if (longitude < -180 || longitude > 180) {
      throw new Error('Longitude must be between -180 and 180 degrees');
    }
    
    // Check for reasonable date range (not too far in past/future)
    const now = new Date();
    const yearDiff = Math.abs(date.getFullYear() - now.getFullYear());
    if (yearDiff > 100) {
      throw new Error('Date is too far from current date for accurate calculations');
    }
  }

  /**
   * Validate time inputs for delta calculations
   * @private
   * @param {Date} currentTime - Current time to validate
   * @param {SolarTimes} solarTimes - Solar times to validate
   */
  validateTimeInputs(currentTime, solarTimes) {
    if (!currentTime || !(currentTime instanceof Date) || isNaN(currentTime.getTime())) {
      throw new Error('Invalid current time provided');
    }
    
    if (!solarTimes || typeof solarTimes !== 'object') {
      throw new Error('Invalid solar times object provided');
    }
    
    if (!solarTimes.sunrise || !(solarTimes.sunrise instanceof Date) || isNaN(solarTimes.sunrise.getTime())) {
      throw new Error('Invalid sunrise time in solar times object');
    }
    
    if (!solarTimes.sunset || !(solarTimes.sunset instanceof Date) || isNaN(solarTimes.sunset.getTime())) {
      throw new Error('Invalid sunset time in solar times object');
    }
    
    if (!solarTimes.date || !(solarTimes.date instanceof Date) || isNaN(solarTimes.date.getTime())) {
      throw new Error('Invalid date in solar times object');
    }
  }

  /**
   * Generate cache key for coordinate and date combination
   * @private
   * @param {Date} date - Date for calculation
   * @param {number} latitude - Latitude coordinate
   * @param {number} longitude - Longitude coordinate
   * @returns {string} Cache key
   */
  generateCacheKey(date, latitude, longitude) {
    // Normalize date to YYYY-MM-DD format
    const dateStr = date.toISOString().split('T')[0];
    
    // Round coordinates to specified precision to improve cache hits
    const roundedLat = parseFloat(latitude.toFixed(this.config.coordinatePrecision));
    const roundedLng = parseFloat(longitude.toFixed(this.config.coordinatePrecision));
    
    return `${dateStr}_${roundedLat}_${roundedLng}`;
  }

  /**
   * Get cached calculation if available and valid
   * @private
   * @param {string} cacheKey - Cache key
   * @returns {SolarTimes|null} Cached solar times or null
   */
  getCachedCalculation(cacheKey) {
    // Check in-memory cache first
    const memoryResult = this.calculationCache.get(cacheKey);
    if (memoryResult && !this.isCacheExpired(memoryResult)) {
      return memoryResult.data;
    }
    
    // Check persistent cache if available
    if (this.cacheService) {
      try {
        const persistentResult = this.cacheService.getSolarCalculation(cacheKey);
        if (persistentResult) {
          // Store in memory cache for faster access
          this.calculationCache.set(cacheKey, {
            data: persistentResult,
            timestamp: new Date()
          });
          return persistentResult;
        }
      } catch (error) {
        console.warn('Failed to get cached calculation from persistent storage:', error);
      }
    }
    
    return null;
  }

  /**
   * Cache calculation result
   * @private
   * @param {string} cacheKey - Cache key
   * @param {SolarTimes} solarTimes - Solar times to cache
   */
  cacheCalculation(cacheKey, solarTimes) {
    // Store in memory cache
    this.calculationCache.set(cacheKey, {
      data: solarTimes,
      timestamp: new Date()
    });
    
    // Manage cache size
    if (this.calculationCache.size > this.config.maxCacheSize) {
      this.evictOldestCacheEntries();
    }
    
    // Store in persistent cache if available
    if (this.cacheService) {
      try {
        this.cacheService.storeSolarCalculation(cacheKey, solarTimes);
      } catch (error) {
        console.warn('Failed to store calculation in persistent cache:', error);
      }
    }
  }

  /**
   * Check if cache entry has expired
   * @private
   * @param {Object} cacheEntry - Cache entry with timestamp
   * @returns {boolean} True if expired
   */
  isCacheExpired(cacheEntry) {
    if (!cacheEntry || !cacheEntry.timestamp) {
      return true;
    }
    
    const age = Date.now() - cacheEntry.timestamp.getTime();
    return age > this.config.cacheExpiration;
  }

  /**
   * Evict oldest cache entries to maintain size limit
   * @private
   */
  evictOldestCacheEntries() {
    const entries = Array.from(this.calculationCache.entries());
    
    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    // Remove oldest 25% of entries
    const toRemove = Math.floor(entries.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      this.calculationCache.delete(entries[i][0]);
    }
    
    console.log(`Evicted ${toRemove} oldest cache entries`);
  }

  /**
   * Normalize date for calculation to handle timezone and midnight rollovers
   * @private
   * @param {Date} date - Date to normalize
   * @returns {Date} Normalized date
   */
  normalizeDateForCalculation(date) {
    // Create a new date at the start of the day in local timezone
    // This ensures consistent calculations regardless of time of day
    const normalized = new Date(date);
    normalized.setHours(12, 0, 0, 0); // Use noon to avoid DST issues
    
    return normalized;
  }

  /**
   * Validate SunCalc results for edge cases
   * @private
   * @param {Object} times - SunCalc times object
   * @param {Date} date - Date used for calculation
   * @param {number} latitude - Latitude coordinate
   * @param {number} longitude - Longitude coordinate
   */
  validateSunCalcResults(times, date, latitude, longitude) {
    if (!times || typeof times !== 'object') {
      throw new Error('SunCalc returned invalid results');
    }
    
    // Check for invalid dates (NaN)
    if (!times.sunrise || isNaN(times.sunrise.getTime())) {
      // This can happen in polar regions during certain seasons
      if (Math.abs(latitude) > 66.5) { // Arctic/Antarctic circles
        console.warn(`No sunrise calculated for polar region at ${latitude}°, ${longitude}° on ${date.toDateString()}`);
      } else {
        throw new Error('SunCalc returned invalid sunrise time');
      }
    }
    
    if (!times.sunset || isNaN(times.sunset.getTime())) {
      // This can happen in polar regions during certain seasons
      if (Math.abs(latitude) > 66.5) { // Arctic/Antarctic circles
        console.warn(`No sunset calculated for polar region at ${latitude}°, ${longitude}° on ${date.toDateString()}`);
      } else {
        throw new Error('SunCalc returned invalid sunset time');
      }
    }
    
    // Validate that sunset is after sunrise (when both are valid)
    if (times.sunrise && times.sunset && 
        !isNaN(times.sunrise.getTime()) && !isNaN(times.sunset.getTime())) {
      if (times.sunset.getTime() <= times.sunrise.getTime()) {
        console.warn(`Sunset (${times.sunset}) is not after sunrise (${times.sunrise}) for ${latitude}°, ${longitude}°`);
      }
    }
  }

  /**
   * Handle edge cases for polar regions and invalid times
   * @private
   * @param {SolarTimes} solarTimes - Solar times object to modify
   * @param {number} latitude - Latitude coordinate
   * @param {number} longitude - Longitude coordinate
   * @param {Date} date - Date for calculation
   */
  handleEdgeCases(solarTimes, latitude, longitude, date) {
    // Handle polar regions where sun may not rise or set
    if (Math.abs(latitude) > 66.5) { // Arctic/Antarctic circles
      
      // If sunrise is invalid (polar night)
      if (isNaN(solarTimes.sunrise.getTime())) {
        // Set sunrise to null or a special value
        solarTimes.sunrise = null;
        console.log(`Polar night: No sunrise at ${latitude}°, ${longitude}° on ${date.toDateString()}`);
      }
      
      // If sunset is invalid (midnight sun)
      if (isNaN(solarTimes.sunset.getTime())) {
        // Set sunset to null or a special value
        solarTimes.sunset = null;
        console.log(`Midnight sun: No sunset at ${latitude}°, ${longitude}° on ${date.toDateString()}`);
      }
    }
    
    // Ensure times are valid Date objects or null
    if (solarTimes.sunrise && isNaN(solarTimes.sunrise.getTime())) {
      solarTimes.sunrise = null;
    }
    
    if (solarTimes.sunset && isNaN(solarTimes.sunset.getTime())) {
      solarTimes.sunset = null;
    }
  }

  /**
   * Handle date boundary cases for time delta calculations
   * @private
   * @param {number} timeDiff - Time difference in milliseconds
   * @param {Date} currentTime - Current time
   * @param {Date} referenceTime - Reference time (sunrise/sunset)
   * @returns {number} Adjusted time difference
   */
  handleDateBoundaries(timeDiff, currentTime, referenceTime) {
    // For most cases, the simple time difference is correct
    // Only adjust if we detect a clear date boundary issue
    
    const oneDayMs = 24 * 60 * 60 * 1000;
    
    // If the absolute difference is more than 20 hours, it might be a date boundary issue
    if (Math.abs(timeDiff) > 20 * 60 * 60 * 1000) {
      // Check if we're dealing with times on adjacent days
      const currentDate = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate());
      const referenceDate = new Date(referenceTime.getFullYear(), referenceTime.getMonth(), referenceTime.getDate());
      
      const dayDiff = Math.round((currentDate.getTime() - referenceDate.getTime()) / oneDayMs);
      
      // If dates are exactly one day apart, don't adjust - the large difference is correct
      if (Math.abs(dayDiff) === 1) {
        return timeDiff;
      }
      
      // For other cases, adjust by the day difference
      if (dayDiff !== 0) {
        return timeDiff - (dayDiff * oneDayMs);
      }
    }
    
    return timeDiff;
  }

  /**
   * Format time delta into human-readable description
   * @private
   * @param {number} hours - Hours component
   * @param {number} minutes - Minutes component
   * @param {boolean} isPast - Whether the reference time has passed
   * @param {string} reference - Reference point ('sunrise' or 'sunset')
   * @returns {string} Formatted description
   */
  formatTimeDelta(hours, minutes, isPast, reference) {
    // Handle zero time case
    if (hours === 0 && minutes === 0) {
      return `Now is ${reference}`;
    }
    
    // Build time string
    let timeStr = '';
    if (hours > 0) {
      timeStr += `${hours}h`;
      if (minutes > 0) {
        timeStr += ` ${minutes}m`;
      }
    } else {
      timeStr = `${minutes}m`;
    }
    
    // Build description based on past/future and reference
    if (isPast) {
      return `${timeStr} after ${reference}`;
    } else {
      return `${timeStr} until ${reference}`;
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.clearCache();
    this.cacheService = null;
  }
}

export default SolarCalculator;